import { supabase } from '../lib/supabase';

export const systemAuditService = {
  /**
   * Get comprehensive audit dashboard statistics
   */
  async getDashboardStats(tenantId = null, startDate = null, endDate = null) {
    try {
      const { data, error } = await supabase?.rpc('get_audit_dashboard_stats', {
          p_tenant_id: tenantId,
          p_start_date: startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString(),
          p_end_date: endDate || new Date()?.toISOString()
        });

      if (error) throw error;

      return {
        totalAuditLogs: data?.[0]?.total_audit_logs || 0,
        totalErrors: data?.[0]?.total_errors || 0,
        criticalErrors: data?.[0]?.critical_errors || 0,
        rlsPolicyDenials: data?.[0]?.rls_policy_denials || 0,
        activeAlerts: data?.[0]?.active_alerts || 0,
        errorRatePercentage: data?.[0]?.error_rate_percentage || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Get RLS policy logs with filters
   */
  async getRlsPolicyLogs(filters = {}) {
    try {
      let query = supabase?.from('rls_policy_logs')?.select(`
          *,
          user:user_profiles(email, first_name, last_name)
        `)?.order('created_at', { ascending: false });

      if (filters?.tableName) {
        query = query?.eq('table_name', filters?.tableName);
      }
      if (filters?.decision) {
        query = query?.eq('decision', filters?.decision);
      }
      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        tenantId: log?.tenant_id,
        policyName: log?.policy_name,
        tableName: log?.table_name,
        operation: log?.operation,
        userId: log?.user_id,
        user: log?.user,
        decision: log?.decision,
        reason: log?.reason,
        executionTimeMs: log?.execution_time_ms,
        contextData: log?.context_data,
        createdAt: log?.created_at
      }));
    } catch (error) {
      console.error('Error fetching RLS policy logs:', error);
      throw error;
    }
  },

  /**
   * Get system errors with filters
   */
  async getSystemErrors(filters = {}) {
    try {
      let query = supabase?.from('system_errors')?.select(`
          *,
          affected_user:user_profiles!system_errors_affected_user_id_fkey(email, first_name, last_name),
          resolved_by_user:user_profiles!system_errors_resolved_by_fkey(email, first_name, last_name)
        `)?.order('created_at', { ascending: false });

      if (filters?.errorType) {
        query = query?.eq('error_type', filters?.errorType);
      }
      if (filters?.severity) {
        query = query?.in('severity', Array.isArray(filters?.severity) ? filters?.severity : [filters?.severity]);
      }
      if (filters?.resolutionStatus) {
        query = query?.eq('resolution_status', filters?.resolutionStatus);
      }
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(err => ({
        id: err?.id,
        tenantId: err?.tenant_id,
        errorType: err?.error_type,
        errorMessage: err?.error_message,
        errorCode: err?.error_code,
        severity: err?.severity,
        stackTrace: err?.stack_trace,
        affectedUserId: err?.affected_user_id,
        affectedUser: err?.affected_user,
        affectedFeature: err?.affected_feature,
        contextData: err?.context_data,
        resolutionStatus: err?.resolution_status,
        resolvedBy: err?.resolved_by,
        resolvedByUser: err?.resolved_by_user,
        resolvedAt: err?.resolved_at,
        resolutionNotes: err?.resolution_notes,
        superAdminNotified: err?.super_admin_notified,
        notificationSentAt: err?.notification_sent_at,
        ipAddress: err?.ip_address,
        userAgent: err?.user_agent,
        createdAt: err?.created_at,
        updatedAt: err?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching system errors:', error);
      throw error;
    }
  },

  /**
   * Get Super Admin alerts
   */
  async getSuperAdminAlerts(filters = {}) {
    try {
      let query = supabase?.from('super_admin_alerts')?.select(`
          *,
          acknowledged_by_user:user_profiles!super_admin_alerts_acknowledged_by_fkey(email, first_name, last_name),
          resolved_by_user:user_profiles!super_admin_alerts_resolved_by_fkey(email, first_name, last_name)
        `)?.order('created_at', { ascending: false });

      if (filters?.alertType) {
        query = query?.in('alert_type', Array.isArray(filters?.alertType) ? filters?.alertType : [filters?.alertType]);
      }
      if (filters?.severity) {
        query = query?.in('severity', Array.isArray(filters?.severity) ? filters?.severity : [filters?.severity]);
      }
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(alert => ({
        id: alert?.id,
        alertType: alert?.alert_type,
        title: alert?.title,
        description: alert?.description,
        severity: alert?.severity,
        sourceType: alert?.source_type,
        sourceId: alert?.source_id,
        tenantId: alert?.tenant_id,
        affectedUsersCount: alert?.affected_users_count,
        status: alert?.status,
        acknowledgedBy: alert?.acknowledged_by,
        acknowledgedByUser: alert?.acknowledged_by_user,
        acknowledgedAt: alert?.acknowledged_at,
        resolvedBy: alert?.resolved_by,
        resolvedByUser: alert?.resolved_by_user,
        resolvedAt: alert?.resolved_at,
        resolutionNotes: alert?.resolution_notes,
        metadata: alert?.metadata,
        createdAt: alert?.created_at,
        updatedAt: alert?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching Super Admin alerts:', error);
      throw error;
    }
  },

  /**
   * Update alert status
   */
  async updateAlertStatus(alertId, status, notes = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {
        status,
        updated_at: new Date()?.toISOString()
      };

      if (status === 'acknowledged') {
        updateData.acknowledged_by = user?.id;
        updateData.acknowledged_at = new Date()?.toISOString();
      } else if (status === 'resolved') {
        updateData.resolved_by = user?.id;
        updateData.resolved_at = new Date()?.toISOString();
        if (notes) updateData.resolution_notes = notes;
      }

      const { data, error } = await supabase?.from('super_admin_alerts')?.update(updateData)?.eq('id', alertId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating alert status:', error);
      throw error;
    }
  },

  /**
   * Update error resolution status
   */
  async updateErrorStatus(errorId, status, notes = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData = {
        resolution_status: status,
        updated_at: new Date()?.toISOString()
      };

      if (status === 'resolved') {
        updateData.resolved_by = user?.id;
        updateData.resolved_at = new Date()?.toISOString();
        if (notes) updateData.resolution_notes = notes;
      }

      const { data, error } = await supabase?.from('system_errors')?.update(updateData)?.eq('id', errorId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        resolutionStatus: data?.resolution_status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating error status:', error);
      throw error;
    }
  },

  /**
   * Log a new system error
   */
  async logError(errorData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

      const { data, error } = await supabase?.from('system_errors')?.insert({
          tenant_id: profile?.tenant_id,
          error_type: errorData?.errorType,
          error_message: errorData?.errorMessage,
          error_code: errorData?.errorCode,
          severity: errorData?.severity,
          stack_trace: errorData?.stackTrace,
          affected_user_id: user?.id,
          affected_feature: errorData?.affectedFeature,
          context_data: errorData?.contextData || {},
          ip_address: errorData?.ipAddress,
          user_agent: errorData?.userAgent
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error logging system error:', error);
      throw error;
    }
  }
};